import { Locale, getLocale, setLocale } from '../i18n';

const KEY = 'yodajjom.settings.v1';

export interface AppSettingsShape {
  bgmEnabled: boolean;
  sfxEnabled: boolean;
  /** 0..100 — 한 슬라이더로 마스터 볼륨. Phase β에서 audio bus에 곱해서 사용. */
  masterVolume: number;
  /** i18n 로케일 — 단일 source of truth는 i18n module. get() 시점에 getLocale()로 합성. */
  locale: Locale;
}

const DEFAULT: AppSettingsShape = {
  bgmEnabled: true,
  sfxEnabled: true,
  masterVolume: 80,
  locale: 'en',
};

type PersistedSettings = Omit<AppSettingsShape, 'locale'>;

class SettingsStore {
  private value: PersistedSettings = {
    bgmEnabled: DEFAULT.bgmEnabled,
    sfxEnabled: DEFAULT.sfxEnabled,
    masterVolume: DEFAULT.masterVolume,
  };
  private loaded = false;

  load() {
    if (this.loaded) return;
    this.loaded = true;
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<AppSettingsShape>;
        this.value = {
          bgmEnabled: typeof parsed.bgmEnabled === 'boolean' ? parsed.bgmEnabled : DEFAULT.bgmEnabled,
          sfxEnabled: typeof parsed.sfxEnabled === 'boolean' ? parsed.sfxEnabled : DEFAULT.sfxEnabled,
          masterVolume: typeof parsed.masterVolume === 'number'
            ? Math.max(0, Math.min(100, parsed.masterVolume))
            : DEFAULT.masterVolume,
        };
      }
    } catch (err) {
      console.warn('AppSettings load failed', err);
    }
  }

  private persist() {
    try {
      localStorage.setItem(KEY, JSON.stringify(this.value));
    } catch (err) {
      console.warn('AppSettings save failed', err);
    }
  }

  get(): AppSettingsShape {
    this.load();
    return { ...this.value, locale: getLocale() };
  }

  setBgm(enabled: boolean) {
    this.load();
    this.value.bgmEnabled = enabled;
    this.persist();
  }
  setSfx(enabled: boolean) {
    this.load();
    this.value.sfxEnabled = enabled;
    this.persist();
  }
  setVolume(v: number) {
    this.load();
    const n = Number.isFinite(v) ? Math.round(v) : DEFAULT.masterVolume;
    this.value.masterVolume = Math.max(0, Math.min(100, n));
    this.persist();
  }
  setLocale(loc: Locale) {
    setLocale(loc);
    // no persist — locale is owned by i18n (yodajjom.locale.v1)
  }
}

export const appSettings = new SettingsStore();
